import { useState, type FormEvent } from "react";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { forgotPassword, resetPassword } from "./api";

const MIN_PASSWORD = 12;
const input =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Self-service password reset on the login page.
 *  - No token  → ask for an email and send a reset link.
 *  - With token (from the emailed link, ?reset_token=…) → set a new password.
 */
export function ForgotPasswordForm({
  onBack,
  resetToken,
}: {
  onBack: () => void;
  resetToken?: string | undefined;
}) {
  const isReset = Boolean(resetToken);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submitRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await forgotPassword(email.trim());
      setDone(res.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the reset link.");
    } finally {
      setLoading(false);
    }
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD || password !== confirm) return;
    setLoading(true);
    try {
      const res = await resetPassword(resetToken!, password);
      setDone(res.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset the password.");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="space-y-4 rounded-lg border border-border bg-card p-6 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-sla-ok" aria-hidden="true" />
        <p className="text-sm">{done}</p>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6">
      <p className="text-sm text-muted-foreground">
        {isReset
          ? "Choose a new password for your account."
          : "Enter your account email and we'll send you a link to reset your password."}
      </p>

      {isReset ? (
        <form onSubmit={submitReset} className="space-y-3" noValidate>
          <div className="space-y-1.5">
            <label htmlFor="new_pw" className="text-sm font-medium">
              New password (min {MIN_PASSWORD} characters)
            </label>
            <input
              id="new_pw"
              type="password"
              autoComplete="new-password"
              className={input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {tooShort && (
              <p className="text-xs text-destructive">At least {MIN_PASSWORD} characters.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="confirm_pw" className="text-sm font-medium">
              Confirm password
            </label>
            <input
              id="confirm_pw"
              type="password"
              autoComplete="new-password"
              className={input}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {mismatch && <p className="text-xs text-destructive">Passwords don't match.</p>}
          </div>

          {error && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || password.length < MIN_PASSWORD || password !== confirm}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loading ? "Saving…" : "Set new password"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitRequest} className="space-y-3" noValidate>
          <div className="space-y-1.5">
            <label htmlFor="fp_email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="fp_email"
              type="email"
              autoComplete="username"
              className={input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {error && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to sign in
      </button>
    </div>
  );
}
